import { useMutation } from '@tanstack/react-query'

import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'
import { queryClient } from '@services/queryClient'

import { usePutTask } from './put'
interface IPutTaskParams {
  id: number
  task: IGenericTask
}

export function useUpdateTaskResource() {
  const { putTask } = usePutTask()
  const { selectedDate } = useSharedDates()
  const { getWeekRange } = useDate()

  const { weekStart } = getWeekRange(selectedDate)

  return useMutation({
    mutationFn: putTask,
    onMutate: handleOptimisticTaskUpdate,
    onError: (_err, _newTask, context) => {
      if (!context) return

      const { previousTask, taskKey } = context
      queryClient.setQueryData(taskKey, previousTask)
    },
    onSettled: (_tasks, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.taskKey,
      }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['TASKS', weekStart.getTime()],
        type: 'active',
      })
    },
  })

  async function handleOptimisticTaskUpdate({ task, id }: IPutTaskParams) {
    await queryClient.cancelQueries({ queryKey: ['TASK', id] })

    const [taskQuery] = queryClient.getQueriesData<IGenericTask>({
      queryKey: ['TASK', id],
      type: 'active',
    })

    if (!taskQuery) return

    const [taskKey, taskResult] = taskQuery
    const previousTask = taskResult

    const updatedTask = task

    queryClient.setQueryData(taskKey, () => updatedTask)

    return { taskKey, previousTask, updatedTask }
  }
}
